<template>
  <o-dialog
    ref="dialog"
    :title="title"
    :info="info"
    :options="[
      { text: $t('general.back'), type: 'cancel' },
      { text: $t('general.confirmDelete'), type: 'confirm', variant: 'danger', callback: confirm },
    ]"
    :portal="portal"
  >
    <div v-if="isRecurring" class="pt-4 text-left">
      <o-field-group>
        <checkbox-field v-model="future" :label="$t('components.reservation.actions.delete.deleteFuture')" :info="$t('components.reservation.actions.delete.deleteFutureInfo')" />
      </o-field-group>
    </div>
  </o-dialog>
</template>

<script setup lang="ts">
import { CheckboxField } from '@/components/fields'

import type { Reservation } from '~/_api/models/reservation'

interface Props {
  reservation: Reservation
  portal?: string
  loading?: boolean
}

interface Emits {
  (e: 'update:loading', value: boolean): void
  (e: 'success'): void
  (e: 'error'): void
}

const props = withDefaults(defineProps<Props>(), {
  portal: undefined,
  loading: false,
})

const emit = defineEmits<Emits>()
const { $api, $i18n, $notification, $bus } = useNuxtApp()

const dialog = ref<any>()
const future = ref(false)

const title = computed(() => {
  return (props.reservation as any).reservation
    ? $i18n.t('components.reservation.actions.delete.removeCustomerFromClass')
    : $i18n.t('components.reservation.actions.delete.title')
})

const info = computed(() => {
  return `${props.reservation.infoLabel || (props.reservation as any).reservation?.infoLabel}`
})

const isRecurring = computed(() => {
  return props.reservation.isRecurring
})

const open = () => {
  dialog.value.open()
}

const confirm = async () => {
  emit('update:loading', true)

  try {
    const reservation = props.reservation as any
    const isAttendee = reservation.idReservationCustomer != null
    const reservationId = reservation.idReservation?.toString()
    const reservationCustomerId = reservation.idReservationCustomer?.toString()
    const date = reservation.date?.integer ?? reservation.date?.value ?? reservation.date

    const response = isAttendee
      ? await $api.reservationCustomer.deleteReservationCustomer(reservationCustomerId, date, { future: future.value })
      : await $api.reservation.deleteReservation(reservationId, date, { future: future.value })

    if (response.success) {
      emit('success')
      emit('update:loading', false)

      $bus.emit.deleted(isAttendee ? 'ReservationCustomer' : 'Reservation', props.reservation)

      $notification.toast({
        title: $i18n.t('components.reservation.actions.delete.success'),
        text: $i18n.t('components.reservation.actions.delete.successInfo'),
      })

      return true
    }
  } catch (e: any) {
    console.log(e)

    emit('error')
    $notification.toast({
      title: $i18n.t('general.error'),
      text: $i18n.t('components.reservation.actions.delete.errorInfo'),
    })
  }

  emit('update:loading', false)
}

defineExpose({ open })
</script>
